import {
  cardTitle,
  grayColor,
  whiteColor,
} from "assets/jss/material-dashboard-pro-react.js";

const documentationStyle = (theme) => ({
  pageContainer: {
    marginTop: "-40px",
    maxWidth: "100%",
    boxSizing: "border-box",
  },
  cardIconTitle: {
    ...cardTitle,
    marginTop: "15px",
    marginBottom: "0px",
  },
  cardBody: {
    padding: "0 15px 3px 15px",
  },
  tableSpinner: {
    position: "absolute",
    marginLeft: "-5px",
    backgroundColor: whiteColor,
    width: "100%",
    minHeight: "50px",
    "& > div": {
      position: "absolute",
      top: "45%",
      left: "50%",
      transform: "translate(-50%, -50%)",
    },
  },
  subAccordion: {
    paddingLeft: "1rem",
  },
  accordionTitle: {
    color: grayColor[7],
    "&:hover,&:focus": {
      color: grayColor[6],
    },
    paddingRight: "40px",
  },
  expandIcon: {
    color: grayColor[7],
    "&:hover,&:focus": {
      color: grayColor[6],
    },
  },
  accordionContent: {
    lineHeight: "1.6",
    paddingRight: "clamp(10px, 3vw, 50px)",
    "& p, & ol, & ul, & li": {
      lineHeight: "1.6",
      marginTop: "12px",
      marginBottom: "12px",
    },
    "& ul, & ol": {
      paddingLeft: "1.5em",
      marginTop: "0",
      marginBottom: "0",
    },
  },

  // =============================================
  // ESTILOS DE DOCUMENTAÇÃO V2
  // =============================================

  subtopic: {
    // Container principal do subtópico
    maxWidth: "100%",
    overflowX: "auto",
    boxSizing: "border-box",
    "& .doc-section-title": {
      fontSize: "15px",
      fontWeight: 600,
      color: grayColor[2],
      marginTop: "24px",
      marginBottom: "6px",
      paddingBottom: "4px",
      borderBottom: `2px solid ${theme.palette.primary.main}`,
      display: "inline-block",
      "&:first-child": {
        marginTop: "8px",
      },
    },

    "& .doc-subsection-title": {
      fontSize: "14px",
      fontWeight: 600,
      color: grayColor[3],
      marginTop: "20px",
      marginBottom: "10px",
    },

    // Bloco de fórmula
    "& .doc-formula": {
      backgroundColor: "#353534",
      color: "#d4d4d4",
      padding: "16px clamp(12px, 2vw, 20px)",
      borderRadius: "6px",
      fontFamily: "Consolas, Monaco, 'Courier New', monospace !important",
      fontSize: "clamp(11px, 1.2vw, 13px)",
      lineHeight: "1.7",
      overflowX: "auto",
      WebkitOverflowScrolling: "touch",
      whiteSpace: "pre",
      margin: "12px 0",
      border: "1px solid #444",
      boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
      maxWidth: "100%",
      boxSizing: "border-box",
      "& *": {
        fontFamily: "Consolas, Monaco, 'Courier New', monospace !important",
      },
      // Syntax highlighting
      "& .formula-keyword": {
        color: "#569cd6",
      },
      "& .formula-function": {
        color: "#dcdcaa",
      },
      "& .formula-comment": {
        color: "#6a9955",
        fontStyle: "italic",
      },
      "& .formula-variable": {
        color: "#9cdcfe",
      },
      "& .formula-operator": {
        color: "#d4d4d4",
      },
    },

    // Tabelas - display block + overflowX auto permite scroll
    // horizontal independente em cada tabela, sem afetar o texto ao redor
    "& .doc-table": {
      display: "block",
      overflowX: "auto",
      WebkitOverflowScrolling: "touch",
      borderCollapse: "collapse",
      margin: "16px 0",
      fontSize: "14px",
      boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
      borderRadius: "6px",
      "& thead": {
        backgroundColor: theme.palette.primary.main,
        "& th": {
          color: whiteColor,
          fontWeight: 600,
          padding: "8px clamp(8px, 1.5vw, 16px)",
          textAlign: "left",
          borderBottom: "none",
          whiteSpace: "nowrap",
        },
      },
      "& tbody": {
        "& tr": {
          backgroundColor: whiteColor,
          transition: "background-color 0.15s ease",
          "&:nth-child(even)": {
            backgroundColor: "#f8f9fa",
          },
          "&:hover": {
            backgroundColor: "#e9ecef",
          },
        },
        "& td": {
          padding: "6px clamp(8px, 1.5vw, 16px)",
          textAlign: "left",
          borderBottom: `1px solid #e9ecef`,
          verticalAlign: "top",
        },
      },
    },

    // Tabela que ocupa toda a largura (opcional)
    "& .doc-table-full": {
      width: "100%",
    },

    // Código inline
    "& code": {
      backgroundColor: "#f1f3f4",
      color: "#c7254e",
      padding: "2px 6px",
      borderRadius: "4px",
      fontFamily: "Consolas, Monaco, 'Courier New', monospace !important",
      fontSize: "13px",
      fontWeight: 500,
    },

    // Lista ordenada estilizada (steps)
    "& .doc-steps": {
      listStyle: "none",
      paddingLeft: "0",
      counterReset: "step-counter",
      "& > li": {
        counterIncrement: "step-counter",
        paddingLeft: "36px",
        position: "relative",
        marginBottom: "12px",
        "&::before": {
          content: "counter(step-counter)",
          position: "absolute",
          left: "0",
          top: "0",
          width: "24px",
          height: "24px",
          borderRadius: "50%",
          backgroundColor: theme.palette.primary.main,
          color: whiteColor,
          fontSize: "12px",
          fontWeight: 600,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        },
      },
    },

    // Lista simples com bullets estilizados
    "& .doc-list": {
      listStyle: "none",
      paddingLeft: "0",
      "& > li": {
        paddingLeft: "20px",
        position: "relative",
        marginBottom: "8px",
        "&::before": {
          content: "''",
          position: "absolute",
          left: "0",
          top: "8px",
          width: "6px",
          height: "6px",
          borderRadius: "50%",
          backgroundColor: theme.palette.primary.main,
        },
      },
    },

    // Destaque/Callout
    "& .doc-highlight": {
      backgroundColor: "#e8f4fd",
      borderLeft: `4px solid ${theme.palette.primary.main}`,
      padding: "12px 16px",
      borderRadius: "0 6px 6px 0",
      margin: "16px 0",
      "& p": {
        margin: "0",
      },
    },

    // Nota/Aviso
    "& .doc-note": {
      backgroundColor: "#fff8e6",
      borderLeft: "4px solid #ffc107",
      padding: "12px 16px",
      borderRadius: "0 6px 6px 0",
      margin: "16px 0",
      fontSize: "13px",
      "& p": {
        margin: "0",
      },
    },

    // Badge/Tag
    "& .doc-badge": {
      display: "inline-block",
      padding: "2px 8px",
      borderRadius: "12px",
      fontSize: "11px",
      fontWeight: 600,
      textTransform: "uppercase",
      letterSpacing: "0.5px",
      "&.badge-input": {
        backgroundColor: "#e3f2fd",
        color: "#1565c0",
      },
      "&.badge-column": {
        backgroundColor: "#f3e5f5",
        color: "#7b1fa2",
      },
      "&.badge-filter": {
        backgroundColor: "#fff3e0",
        color: "#e65100",
      },
    },

    // Grid de cards para dimensões
    "& .doc-dimension-grid": {
      display: "grid",
      gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
      gap: "16px",
      margin: "16px 0",
    },

    "& .doc-dimension-card": {
      backgroundColor: "#f8f9fa",
      borderRadius: "8px",
      padding: "16px",
      border: "1px solid #e9ecef",
      "& .dimension-title": {
        fontWeight: 600,
        color: grayColor[2],
        marginBottom: "8px",
        fontSize: "14px",
      },
      "& .dimension-list": {
        listStyle: "none",
        padding: "0",
        margin: "0",
        "& li": {
          fontSize: "13px",
          padding: "4px 0",
          color: grayColor[2],
          borderBottom: "1px solid #e9ecef",
          "&:last-child": {
            borderBottom: "none",
          },
          "& strong": {
            color: grayColor[1],
          },
        },
      },
    },

    // Separador
    "& .doc-divider": {
      height: "1px",
      backgroundColor: "#e9ecef",
      margin: "24px 0",
      border: "none",
    },
  },
});

export default documentationStyle;
